import { FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING } from "../../../../../components/Form/FormItems/FormItemResourceSelectLazyLoading";
import { FORM_ITEM_DATE_PICKER } from "../../../../../components/Form/FormItems/FormItemDatePicker";
import { FORM_ITEM_RESOURCE_SELECT } from "../../../../../components/Form/FormItems/FormItemResourceSelect";
import { FORM_ITEM_TEXT } from "../../../../../components/Form/FormItems/FormItemText";
import { FORM_TYPE_UPLOAD_FILE } from "../../../../../components/Form/FormItems/FormItemUploadFile";
import config from "../../../../../config";
import { createDisabledDateBefore, createDisabledDateAfter } from "../../User/UserFormSteps/datePeriodHelpers";

/** SmartTable key for the user-profile qualification history table. */
export const TABLE_USER_VIEW_TRG_QUALIFICATIONS_NAME = "table-user-view-trg-qualifications";

/**
 * Generates the form field configuration for the add/edit qualification history modal.
 *
 * Fields: Training (qualification selector), Completed Date, Validity Start Date,
 * Instructor Type (internal/external), Instructor or External Provider + Name,
 * Evidence file upload, Certificate file upload.
 *
 * @param {object} [data={}] - Existing qualification history record for pre-populating.
 * @param {number} userId - ID of the user; used to scope the qualification API query.
 * @param {object} form - Ant Design Form instance for cross-field date validation.
 * @param {Function} t - i18next translation function.
 * @returns {Array<object>} Array of dynamic form field descriptors.
 */
export const generateFormFields = (userId, form, t, data = {}) => {
  return [
    {
      required: true,
      type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
      name: "qualification_id",
      label: "trg.add_qualification_modal.training",
      placeholder: "trg.add_qualification_modal.training",
      initialValue: data.qualification
        ? {
            id: data.qualification.id,
            text: `${data.qualification.training.name} (${data.qualification.training.code})`
          }
        : undefined,
      apiRoute: `trg/qualifications?field%5B%5D=id&op%5B%5D=eq&term%5B%5D=${userId}&with%5B%5D=qualifications.training.provider`,
      fieldProps: {
        disabled: data?.id,
        transformer: (res) => {
          return (
            res[0]?.qualifications?.map((item) => ({
              ...item,
              value: item.id,
              text: `${item.training.name} (${item.training.code})`
            })) || []
          );
        }
      }
    },
    {
      type: FORM_ITEM_DATE_PICKER,
      required: true,
      dependencies: ["validity_start_date"],
      name: "completed_date",
      label: "trg.add_qualification_modal.completed_date",
      placeholder: "trg.add_qualification_modal.completed_date",
      disabledDate: createDisabledDateBefore(form, "validity_start_date")
    },
    {
      type: FORM_ITEM_DATE_PICKER,
      required: true,
      dependencies: ["completed_date"],
      name: "validity_start_date",
      label: "trg.add_qualification_modal.validity_start_date",
      placeholder: "trg.add_qualification_modal.validity_start_date",
      disabledDate: createDisabledDateAfter(form, "completed_date")
    },
    {
      type: FORM_ITEM_RESOURCE_SELECT,
      required: true,
      name: "instructor_type",
      label: "trg.add_qualification_modal.instructor_type",
      placeholder: "trg.add_qualification_modal.instructor_type",
      options: [
        { value: 1, text: t("trg.add_qualification_modal.internal") },
        { value: 0, text: t("trg.add_qualification_modal.external") }
      ]
    },
    {
      required: true,
      type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
      visible: (formInstance) => formInstance.getFieldValue("instructor_type") === 1,
      name: "instructor_id",
      label: "trg.add_qualification_modal.instructor",
      placeholder: "trg.add_qualification_modal.instructor",
      initialValue: data.instructor,
      apiRoute: "users/all",
      fieldProps: {
        searchColumns: ["name"],
        customParams: {
          "filters[validContract]": 1,
          order_by_column: ["name"],
          order_by_type: ["asc"],
          field: ["user_is_in_organisation"],
          op: ["eq"],
          term: [1]
        }
      }
    },
    {
      required: true,
      type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
      visible: (formInstance) => formInstance.getFieldValue("instructor_type") === 0,
      name: "external_provider_id",
      label: "trg.add_qualification_modal.provider",
      placeholder: "trg.add_qualification_modal.provider",
      apiRoute:
        "trg/external-providers?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
      fieldProps: {
        requestOnMount: !!data.external_provider_id
      }
    },
    {
      required: true,
      type: FORM_ITEM_TEXT,
      visible: (formInstance) => formInstance.getFieldValue("instructor_type") === 0,
      max: 200,
      name: "instructor_name",
      label: "trg.add_qualification_modal.instructor",
      placeholder: "trg.add_qualification_modal.instructor"
    },
    {
      type: FORM_TYPE_UPLOAD_FILE,
      name: "evidence",
      label: "trg.add_qualification_modal.evidence",
      placeholder: "ior.new_report.new_attachment.attachment_button",
      fieldProps: {
        purpose: "qualificationHistory",
        entity: "qualificationHistory",
        type: "document",
        placeholder: t("container.general_words.drag_and_drop_file"),
        dragAndDrop: true
      }
    },
    {
      type: FORM_TYPE_UPLOAD_FILE,
      name: "certificate",
      label: "trg.add_qualification_modal.certificate",
      placeholder: "ior.new_report.new_attachment.attachment_button",
      fieldProps: {
        purpose: "certificate",
        entity: "certificate",
        type: "document",
        placeholder: t("container.general_words.drag_and_drop_file"),
        dragAndDrop: true
      }
    }
  ];
};

/**
 * Transforms raw form values into the API payload for qualification history records.
 * Formats dates and merges evidence + certificate files into a single `evidences` array.
 *
 * @param {object} [values={}] - Raw form values from the qualification history modal.
 * @param {Function} format - App date formatter ({ date, dateFormat, defaultReturn }) => string.
 * @returns {object} API-ready payload object.
 */
export const formTransformer = (format, values = {}) => {
  return {
    ...values,
    completed_date: format({
      date: values.completed_date,
      dateFormat: config.defaultDateFormat,
      defaultReturn: null
    }),
    validity_start_date: format({
      date: values.validity_start_date,
      dateFormat: config.defaultDateFormat,
      defaultReturn: null
    }),
    evidences: [(values.evidence || [])[0], (values.certificate || [])[0]].filter((item) => !!item)
  };
};
